<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild :settings="fields.settings" :container-page="fields.containerPage" />
        </div>

        <div class="mb-4">
          <div class="d-flex align-center mb-2" style="gap: 10px">
            <label class="label-input mb-0">Section Title</label>
            <input type="checkbox" v-model="fields.sectionTitle.show" />
          </div>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.sectionTitle.content" :config="editorConfig" />
            <div class="mt-4">
              <DividerPrimary :divider="fields.dividerHeader" label="Header Divider" />
            </div>
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Layout</label>
          <div class="group-item">
            <v-select
              v-model="fields.layout.gridRatio"
              label="Grid Ratio"
              :items="gridRatioOptions"
              item-text="text"
              item-value="value"
              hide-details
            />
          </div>
        </div>

        <div class="mb-4">
          <div class="d-flex align-center mb-2" style="gap: 10px">
            <label class="label-input mb-0">Side Image</label>
            <input type="checkbox" v-model="fields.sideImage.show" />
          </div>
          <div class="group-item">
            <v-select
              v-model="fields.sideImage.position"
              label="Position"
              :items="positionOptions"
              item-text="text"
              item-value="value"
            />
            <UploadImage
              @callback="handleUploadSideImage"
              :image="{ imageSrc: fields.sideImage.src }"
              :height="`200`"
              :typeUpload="'image'"
              @callbackDelete="handleRemoveSideImage"
            />
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Left Content</label>
          <div class="group-item">
            <p class="mb-2 font-weight-bold">Context Title</p>
            <ckeditor :editor="editor" v-model="fields.context.title" :config="editorConfig" />
            <div class="mt-4">
              <DividerPrimary :divider="fields.dividerContext" label="Context Divider" />
            </div>
            <div class="mt-4">
              <p class="mb-2 font-weight-bold">Context Text</p>
              <ckeditor :editor="editor" v-model="fields.context.text" :config="editorConfig" />
            </div>
          </div>
        </div>

        <div class="mb-4">
          <div class="d-flex align-center mb-2" style="gap: 10px">
            <label class="label-input mb-0">Feature Items</label>
            <input type="checkbox" v-model="fields.features.show" />
          </div>
          <div class="group-item">
            <v-row class="mb-2">
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields.features.backgroundColor"
                  name="featuresBackgroundColor"
                  label="Background Color"
                  default-color="#0130E2"
                />
              </v-col>
              <v-col cols="12" md="6">
                <ColorPicker
                  v-model="fields.features.textColor"
                  name="featuresTextColor"
                  label="Text Color"
                  default-color="#FFFFFF"
                />
              </v-col>
            </v-row>
            <div class="d-flex justify-end mb-2">
              <v-btn small text color="primary" @click="handleAddFeature">
                <v-icon size="16">mdi-plus</v-icon>
                <span>Add Feature</span>
              </v-btn>
            </div>
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.features.items" :key="`feature-item-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <div class="d-flex align-center" style="gap: 10px">
                      <img
                        :src="item.image.src"
                        alt=""
                        style="width: 48px; height: 48px; object-fit: cover; border: 1px solid #e0e0e0; border-radius: 4px;"
                      />
                      <div class="line-clamp-1" v-html="item.content.replace(/<[^>]*>/g, '').trim()"></div>
                    </div>
                    <v-btn icon @click.stop="handleRemoveFeature(index)">
                      <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <label class="mb-2 d-block">Content</label>
                  <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                  <div class="d-flex align-center mt-4 mb-2" style="gap: 10px">
                    <label class="mb-0">Image</label>
                    <input
                      type="checkbox"
                      :checked="item.image.show"
                      @change="e => updateFeatureImageShow(index, !!e.target.checked)"
                    />
                  </div>
                  <UploadImage
                    v-if="item.image.show"
                    @callback="obj => handleUploadFeatureImage(obj, index)"
                    :image="{ imageSrc: item.image.src }"
                    :height="`140`"
                    :typeUpload="'image'"
                    @callbackDelete="() => handleRemoveFeatureImage(index)"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update.js';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';
import DividerPrimary from './common/divider-primary.vue';
import ColorPicker from './common/color-picker.vue';

const defaultFeatureItem = () => ({
  content: '<p><strong>Feature Title</strong></p>',
  image: {
    show: true,
    src: 'https://cdn.skyfastimg.com/images/2026/06/04/image-6a20e173145d8a3b3c0a0e0d.jpg',
  },
});

export default {
  name: 'fastvaluedeal-card-basic-section-editor',
  components: {
    UploadImage,
    SectionSettingsContainerFuild,
    DividerPrimary,
    ColorPicker,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      gridRatioOptions: [
        { text: '2fr 1fr', value: '2fr-1fr' },
        { text: '3fr 2fr', value: '3fr-2fr' },
      ],
      positionOptions: [
        { text: 'Left', value: 'left' },
        { text: 'Right', value: 'right' },
      ],
      fields: {
        title: 'Fastvaluedeal Card Basic',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#F8F8F8',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        sectionTitle: {
          show: false,
          content: '<h2 style="text-align: center"><strong>Never Overpay For TV Again</strong></h2>',
        },
        layout: {
          gridRatio: '2fr-1fr',
        },
        context: {
          title: '<h2><strong>Cutting-Edge Technology For Smarter Television</strong></h2>',
          text:
            '<p>If you want to enjoy high-quality television without the hassle of cables or expensive subscriptions, WaveMax is the perfect solution for you.</p>',
        },
        dividerHeader: {
          show: true,
          maxWidth: 90,
          height: 6,
          backgroundColor: '#5B04DE',
          align: 'center',
        },
        dividerContext: {
          show: true,
          maxWidth: 90,
          height: 6,
          backgroundColor: '#5B04DE',
          align: 'left',
        },
        features: {
          show: true,
          backgroundColor: '#0130E2',
          textColor: '#FFFFFF',
          items: [defaultFeatureItem(), defaultFeatureItem()],
        },
        sideImage: {
          show: true,
          src: '',
          position: 'right',
        },
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        sectionTitle: { ...this.fields.sectionTitle, ...(extract.sectionTitle || {}) },
        layout: { ...this.fields.layout, ...(extract.layout || {}) },
        context: { ...this.fields.context, ...(extract.context || {}) },
        dividerHeader: { ...this.fields.dividerHeader, ...(extract.dividerHeader || {}) },
        dividerContext: { ...this.fields.dividerContext, ...(extract.dividerContext || {}) },
        features: {
          ...this.fields.features,
          ...(extract.features || {}),
          items: Array.isArray(extract.features?.items)
            ? extract.features.items.map(item => ({
                content: item.content || '',
                image: {
                  show: item.image?.show !== false,
                  src: item.image?.src || '',
                },
              }))
            : this.fields.features.items,
        },
        sideImage: { ...this.fields.sideImage, ...(extract.sideImage || {}) },
      };
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const payload = {
        ...this.fields,
        features: {
          ...this.fields.features,
          items: (this.fields.features.items || []).map(item => ({
            content: item.content || '',
            image: {
              show: item.image?.show !== false,
              src: item.image?.src || '',
            },
          })),
        },
      };
      const updateHtml = updateHtmlFromData.sectionFastvaluedealCardBasic(this.sectionEditor.section.html, payload);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddFeature() {
      this.fields.features.items.push(defaultFeatureItem());
    },
    handleRemoveFeature(index) {
      this.fields.features.items.splice(index, 1);
    },
    updateFeatureImageShow(index, show) {
      if (!this.fields.features.items[index]) return;
      this.$set(this.fields.features.items[index].image, 'show', show);
    },
    handleUploadFeatureImage(obj, index) {
      if (!this.fields.features.items[index]) return;
      this.$set(this.fields.features.items[index].image, 'src', obj.imageSrc || '');
    },
    handleRemoveFeatureImage(index) {
      if (!this.fields.features.items[index]) return;
      this.$set(this.fields.features.items[index].image, 'src', '');
    },
    handleUploadSideImage(obj) {
      this.fields.sideImage.src = (obj && obj.imageSrc) || '';
    },
    handleRemoveSideImage() {
      this.fields.sideImage.src = '';
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
::v-deep .ck-editor__editable_inline {
  min-height: 120px !important;
}
</style>
